
import './GenerateTopics.scss';

import React, { FC, useContext, useEffect } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { useAIGenerator } from 'customHooks/useAIGenerator';
import { useDataSet } from 'customHooks/useDataSet';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { Document } from 'models/Document';
import { Entities } from 'models/Entities';
import { GeneratedTopic } from 'models/GeneratedTopic';
import { LogLevel } from 'models/LogLevel';
import { SiteSection } from 'models/SiteSection';
import moment from 'moment';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface GenerateTopicsProps {
  generateModalShow: boolean;
  setGenerateModalShow: (isShowing: boolean) => void;
}

const GenerateTopics: FC<GenerateTopicsProps> = ({ generateModalShow, setGenerateModalShow }) => {
  const { configRepo, selectedProjectId, sendLog, notify, projects } = useContext(AppContext);

  const onTokenExpired = useTokenExpiredHandler();
  const { generateArticleThemes } = useAIGenerator(configRepo!.SCHEDULE_API_BASE_URI);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [generatedTopics, setGeneratedTopics] = React.useState<GeneratedTopic[]>([]);
  const { createEntity: createDocument } = useModifyEntityService<Document>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Documents,
    sendLog,
    onTokenExpired,
  );
  const { dataSet: sectionsDataSet } = useDataSet<SiteSection>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.SiteSections,
    selectedProjectId,
  );
  const sections: SiteSection[] = sectionsDataSet.responseData as SiteSection[];

  const onSelectAllTopics = (e: React.MouseEvent<HTMLInputElement>) => {
    const desiredState = (e.target as any).checked;
    const checkboxListState = [...generatedTopics];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < checkboxListState.length; ++i) {
      checkboxListState[i].checked = desiredState;
    }
    setGeneratedTopics(checkboxListState);
  };

  const onSaveSelectedTopics = async () => {
    const selectedTopics = generatedTopics.filter((t) => t.checked);
    if (selectedTopics.length === 0) {
      return;
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < selectedTopics.length; ++i) {
      const topic = selectedTopics[i];

      const document: Document = {
        content: '',
        statusId: 1,
        projectId: selectedProjectId!,
        siteSectionId: topic.sectionId,
        shortDescription: topic.themeName,
        title: topic.title,
        plannedDate: topic.scheduledDate,
        targetLength: 500,
        keywordId: topic.keywordId,
      } as Document;

      // eslint-disable-next-line no-await-in-loop
      const result = await createDocument(document);
      if (!result) {
        sendLog(`Failed to create document for ${document.title}`, LogLevel.error);
      }
    }
    setGeneratedTopics([]);
    setGenerateModalShow(false);
    window.location.href = window!.location!.href;
  };

  const onRegenerateTopics = async () => {
    setGeneratedTopics([]);
    setIsLoading(true);
    try {
      const project = projects.find(p => p.id === selectedProjectId);
      if (!project) {
        notify('Failed to find currently selected project, select a different project and then retry content generation', NotificationType.Error);
        return;
      }
      const topics = await generateArticleThemes(project, sections);
      setGeneratedTopics(topics);
    } catch (e) {
      notify('Failed to generate themes, please retry by clicking on the "Regenerate" button.', NotificationType.Error);
    }
    setIsLoading(false);
  };
  const onCancelGenerateTopics = () => {
    setGeneratedTopics([]);
    setGenerateModalShow(false);
  };

  useEffect(() => {
    if (generateModalShow) {
      onRegenerateTopics();
    }
  }, [generateModalShow]);

  return (
    <Modal
      show={generateModalShow}
      onHide={() => setGenerateModalShow(false)}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter-topics"
      centered
      id="modalGenerateTopics"
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter-topics">Generate New Topics</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          For the best results, make sure to: <br />
          <ol>
            <li>Add target audience, solution, and negative terms into your <a href="/account" target="_blank">project configuration</a>, and</li>
            <li><a href="/seo" target="_blank">import your sitemap</a> before generating content.</li>
          </ol>
          <strong>This may take up to 4 minutes to complete, please be patient!</strong> <br />
          For more help with topic generation, see this {" "}
          <a href="https://blabberjax.atlassian.net/wiki/x/VIBaGQ" target='_blank' rel="noreferrer">
            help guide
          </a>.
        </Alert>
        <Table>
          <thead>
            <tr>
              <th aria-label="select all">
                <Form.Check // prettier-ignore
                  type="checkbox"
                  id="select-all-topics"
                  onClick={(e) => onSelectAllTopics(e)}
                />
              </th>
              <th>Theme</th>
              <th>Title</th>
              <th>Date</th>
              <th>Keyword</th>
              <th>Search Volume</th>
              <th>Difficulty</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4}>
                  <div id="loadingStatus">
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Loading...</span>
                  </div>
                </td>
              </tr>
            )}
            {generatedTopics.map((p, index) => (
              <tr key={p.id}>
                <td aria-label="select generated topic">
                  <Form.Check // prettier-ignore
                    type="checkbox"
                    id={`select-${p.id}`}
                    className="topic-checkbox"
                    checked={p.checked}
                    onClick={(e) => {
                      const updatedCheckboxList = [...generatedTopics];
                      updatedCheckboxList[index].checked = (e?.target as any).checked;
                      setGeneratedTopics(updatedCheckboxList);
                    }}
                  />
                </td>
                <td>{p.themeName}</td>
                <td>{p.title}</td>
                <td>{moment(p.scheduledDate).format('MM/DD/YYYY')}</td>
                <td>{p.keyword}</td>
                <td>{Number.isNaN(p.searchVolume) ? '' : p.searchVolume}</td>
                <td>{Number.isNaN(p.difficulty) ? '' : p.difficulty}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => onSaveSelectedTopics()}>Save Selected</Button>
        <Button onClick={() => onRegenerateTopics()}>Regenerate</Button>
        <Button onClick={() => onCancelGenerateTopics()}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateTopics;
